<template>
  <div class="easypaisa-page options-card-wrapper--no-padding options-card-wrapper--full-height">
    <div class="easypaisa-page__card">
      <!-- Header -->
      <div class="easypaisa-page__header">
        <div class="easypaisa-page__header-left">
          <div v-if="!isExpired && formatedTime" class="easypaisa-page__timer">
            {{ formatedTime }}
          </div>
        </div>

        <div class="easypaisa-page__logo">
          <img v-if="logo" :src="logo" />
        </div>

        <div class="easypaisa-page__header-right">
          <div class="easypaisa-page__locale" ref="localeMenu" @click="toggleLocaleMenu">
            <span class="easypaisa-page__locale-text">{{ currentLocale.toUpperCase() }}</span>
            <svg
              class="easypaisa-page__locale-icon"
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M4 6L8 10L12 6"
                stroke="#2a2637"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
            <div v-if="localeMenuOpen" class="easypaisa-page__locale-dropdown">
              <div
                v-for="locale in locales"
                :key="locale"
                class="easypaisa-page__locale-item"
                :class="{ active: locale === currentLocale }"
                @click.stop="selectLocale(locale)">
                {{ locale.toUpperCase() }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Amount -->
      <div class="easypaisa-page__amount">
        <span v-if="templateData.symbol" class="easypaisa-page__amount-symbol">
          {{ templateData.symbol }}
        </span>
        <span class="easypaisa-page__amount-value">{{ templateData.formattedAmount }}</span>
        <span v-if="!templateData.symbol" class="easypaisa-page__amount-currency">
          {{ templateData.currency }}
        </span>
      </div>

      <!-- Center section -->
      <div class="easypaisa-page__center">
        <template v-if="!isExpired">
          <div class="easypaisa-page__form-title">
            {{ $t(amountTitle) }}
          </div>

          <div class="easypaisa-page__input-row">
            <div class="easypaisa-page__input-wrapper">
              <input
                :value="displayPhoneValue"
                :disabled="loading"
                :class="{ 'easypaisa-page__input--error': hasValidationError }"
                class="easypaisa-page__input easypaisa-page__input--real"
                type="tel"
                autocomplete="tel"
                inputmode="numeric"
                @input="onPhoneInput"
                @focus="onPhoneFocus"
                @click="snapCursor"
                @blur="validatePhone" />
              <div class="easypaisa-page__input-mask" aria-hidden="true">
                <template v-if="phoneNumber.length === 0">
                  <span class="easypaisa-page__input-mask-placeholder">{{
                    $t('easypaisa__placeholder')
                  }}</span>
                </template>
                <template v-else>
                  <span class="easypaisa-page__input-mask-typed">{{ maskParts.typed }}</span>
                  <span class="easypaisa-page__input-mask-ghost">{{ maskParts.ghost }}</span>
                </template>
              </div>
            </div>
          </div>

          <div
            class="easypaisa-page__hint"
            :class="{ 'easypaisa-page__hint--error': hasValidationError }">
            <template v-if="hasValidationError">
              {{ $t('easypaisa__validation_error') }}
            </template>
            <template v-else>
              {{ $t('easypaisa__hint') }}
            </template>
          </div>
        </template>

        <template v-else>
          <div class="easypaisa-page__expired-text">
            {{ $t('easypaisa__session_timed_out') }}
          </div>
        </template>
      </div>

      <!-- Action buttons -->
      <div class="easypaisa-page__actions">
        <button class="easypaisa-page__btn easypaisa-page__btn--secondary" @click="handleClose">
          {{ $t('easypaisa__close') }}
        </button>
        <div class="easypaisa-page__actions-divider"></div>
        <button
          v-if="!isExpired"
          :disabled="loading || !phoneNumber"
          class="easypaisa-page__btn easypaisa-page__btn--primary"
          @click="confirm">
          <Loader v-if="loading" />
          <span v-else>{{ $t('easypaisa__confirm') }}</span>
        </button>
        <button
          v-else
          class="easypaisa-page__btn easypaisa-page__btn--primary"
          @click="handleTryAgain">
          {{ $t('easypaisa__try_again') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Loader from './components/Loader.vue';
import { createDayJsDurationFormatterFactory } from '@/helpers/DayJsDurationFormatter/DayJsDurationFormatterImpl';
import { messages } from './translations/index';
import parseAvailableLocales from '@/helpers/parseAvailableLocales';
import { createMaskFormatter } from '@/helpers/Formatting/MaskFormatter';
import { createMaskInputTransformer } from '@/helpers/Formatting/MaskInputTransformer';
import EasypaisaIcon from './assets/easypaisa.svg';
import JazzCashIcon from './assets/jazzcash.svg';

const PHONE_REGEX = /^03\d{9}$/;

const phoneMaskFormatter = createMaskFormatter({ maxLength: 11 });
const phoneMaskTransformer = createMaskInputTransformer({ formatter: phoneMaskFormatter });

export default {
  components: { Loader },

  created() {
    this.startTimer(this.templateData.expires);

    if (this.locales.includes(this.selectedLocale)) {
      this.selectLocale(this.selectedLocale);
    } else {
      this.selectLocale(this.locales.length ? this.locales[0] : 'en');
    }

    document.addEventListener('click', this.closeLocaleMenu);
  },

  beforeDestroy() {
    document.removeEventListener('click', this.closeLocaleMenu);
  },

  data() {
    return {
      loading: false,
      phoneNumber: '',
      hasValidationError: false,
      localeMenuOpen: false,
      maskParts: { typed: '', ghost: 'XXXXXXXXXXX' },
      pollCountAfterConfirm: 0,
      watchingForRedirect: false,
    };
  },

  watch: {
    paymentInvoiceData() {
      if (!this.watchingForRedirect) return;
      this.pollCountAfterConfirm++;
      if (this.pollCountAfterConfirm >= 2) {
        this.watchingForRedirect = false;
        this.$router.replace({
          name: 'PaymentStatus',
          params: { id: this.paymentInvoice.id },
        });
      }
    },
  },

  computed: {
    ...mapState(['paymentInvoiceData', 'flowData', 'checkoutOptions']),
    ...mapState('InvoiceLifetime', ['isExpired', 'timeToExpired']),
    ...mapState('UI', ['availableLocales', 'queryLocale']),

    amountTitle() {
      return this.templateData.method_code === 'easypaisa'
        ? 'easypaisa__enter_number'
        : 'easypaisa__enter_number_jazzcash';
    },

    locales() {
      const optionLocales = parseAvailableLocales(
        this.availableLocales,
        this.checkoutOptions?.available_locales,
        Object.keys(messages)
      );

      if (optionLocales.length) {
        return optionLocales;
      }

      return Object.keys(messages);
    },

    logo() {
      if (this.templateData.method_code === 'easypaisa') {
        return EasypaisaIcon;
      } else if (this.templateData.method_code === 'jazzcash') {
        return JazzCashIcon;
      }

      return this.templateData.logo;
    },

    selectedLocale() {
      const { default_locale } = this.paymentInvoice;

      const supportedLocale = locale =>
        Object.keys(messages).some(key => key === locale) ? locale : null;

      return (
        supportedLocale(default_locale) ||
        supportedLocale(this.queryLocale) ||
        supportedLocale(this.checkoutOptions?.locale) ||
        supportedLocale(this.$browserLocale)
      );
    },

    currentLocale() {
      return this.$i18n.locale;
    },

    paymentInvoice() {
      return this.paymentInvoiceData ? this.paymentInvoiceData.data || this.paymentInvoiceData : {};
    },

    dateFormatter() {
      return createDayJsDurationFormatterFactory({
        defaultTemplate: 'HH:mm:ss',
      });
    },

    formatedTime() {
      return this.dateFormatter().format(this.timeToExpired);
    },

    displayPhoneValue() {
      if (this.phoneNumber.length === 0) return '';
      return this.maskParts.typed + this.maskParts.ghost;
    },

    templateData() {
      const { has_return_url, id, expires } = this.paymentInvoice;

      const { amount: mainAmount, currency: mainCurrency } = this.flowData;

      const {
        requisites,
        method,
        amount: payloadAmount,
        currency: payloadCurrency,
      } = this.flowData.payload;

      const parseRequisites = () => {
        if (!requisites) return {};
        if (typeof requisites === 'string') {
          try {
            return JSON.parse(requisites);
          } catch {
            return {};
          }
        }
        return requisites;
      };

      const { default_locale = null } = parseRequisites();

      const amount = [undefined, null].includes(payloadAmount) ? mainAmount : payloadAmount;
      const currency = [undefined, null].includes(payloadCurrency) ? mainCurrency : payloadCurrency;

      const symbol = this.currencySymbol(currency);
      const formattedAmount =
        amount != null ? Number(amount).toLocaleString('en-US', { minimumFractionDigits: 0 }) : '';

      return {
        id,
        has_return_url,
        expires,
        logo: (method || {}).logo || null,
        currency,
        symbol,
        formattedAmount,
        default_locale,
        method_code: method?.code || null,
      };
    },
  },

  methods: {
    ...mapActions(['confirmPaymentInvoice']),
    ...mapActions('InvoiceLifetime', ['startTimer']),

    selectLocale(locale) {
      this.localeMenuOpen = false;
      this.$bus.$emit('changeLocale', locale);
    },

    toggleLocaleMenu() {
      this.localeMenuOpen = !this.localeMenuOpen;
    },

    closeLocaleMenu(event) {
      if (!this.$refs.localeMenu || !this.$refs.localeMenu.contains(event.target)) {
        this.localeMenuOpen = false;
      }
    },

    onPhoneInput(event) {
      const { model, displayParts } = phoneMaskTransformer.transformInput(event.target.value);
      this.phoneNumber = model;
      this.maskParts = displayParts;
      this.hasValidationError = false;
      // Set full display string so text-align:center matches the overlay exactly
      event.target.value = displayParts.typed + displayParts.ghost;
      // Place cursor right after typed digits
      const pos = displayParts.typed.length;
      event.target.setSelectionRange(pos, pos);
    },

    onPhoneFocus(event) {
      this.$nextTick(() => this.snapCursor(event));
    },

    snapCursor(event) {
      const pos = this.phoneNumber.length;
      event.target.setSelectionRange(pos, pos);
    },

    validatePhone() {
      if (!this.phoneNumber) {
        this.hasValidationError = false;
        return;
      }

      this.hasValidationError = !PHONE_REGEX.test(this.phoneNumber);
    },

    async confirm() {
      this.validatePhone();

      if (this.hasValidationError || this.loading) return;

      this.loading = true;
      this.hasValidationError = false;

      const { status } = await this.confirmPaymentInvoice({
        id: this.templateData.id,
        data: { payload: { phone: this.phoneNumber } },
      });

      if (status !== 200) {
        this.loading = false;
        return;
      }

      this.pollCountAfterConfirm = 0;
      this.watchingForRedirect = true;
    },

    handleClose() {
      this.redirectReturnUrl(true);
    },

    handleTryAgain() {
      this.redirectReturnUrl(true);
    },

    redirectReturnUrl(force) {
      if (!this.templateData.has_return_url) return;

      const isForce = force ? '&force=true' : '';
      location.replace(`/return?id=${this.paymentInvoice.id}${isForce}`);
    },

    currencySymbol(currency) {
      switch (currency) {
        case 'PKR':
          return '₨';
        case 'UAH':
          return '₴';
        case 'USD':
          return '$';
        case 'EUR':
          return '€';
        default:
          return null;
      }
    },
  },
};
</script>

<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

.easypaisa-page {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100%;
  padding: 16px 0;

  &__card {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 520px;
    background: #ffffff;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  }

  // ── Header ──────────────────────────────────────────────
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 72px;
    background: #ffffff;
    border-bottom: 2px solid #000000;
    padding: 0;
  }

  &__header-left,
  &__header-right {
    flex: 1;
    display: flex;
    align-items: center;
    min-width: 0;
    padding: 8px 12px;
  }

  &__header-right {
    justify-content: flex-end;
  }

  &__logo {
    width: 150px;
    height: 40px;
    flex-shrink: 0;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }

  &__timer {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 28px;
    min-width: 54px;
    padding: 4px 8px;
    background: #fdb43c;
    border-radius: 4px;
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    color: #1e2128;
    white-space: nowrap;

    &.medium {
      background: #f59300;
    }

    &.low {
      background: #f83833;
      color: #ffffff;
    }
  }

  &__locale {
    position: relative;
    display: flex;
    align-items: center;
    gap: 4px;
    cursor: pointer;
    user-select: none;
  }

  &__locale-text {
    font-family: 'Inter', sans-serif;
    font-weight: 600;
    font-size: 12px;
    line-height: 16px;
    color: #2a2637;
    letter-spacing: 0.12px;
  }

  &__locale-icon {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &__locale-dropdown {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    min-width: 64px;
    background: #ffffff;
    border: 1px solid rgba(0, 0, 0, 0.12);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
    z-index: 100;
    overflow: hidden;
  }

  &__locale-item {
    padding: 8px 12px;
    font-family: 'Inter', sans-serif;
    font-size: 12px;
    font-weight: 600;
    color: #2a2637;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #f4f4f4;
    }

    &.active {
      background: #f0f0f0;
      color: #1e2128;
    }
  }

  // ── Amount ───────────────────────────────────────────────
  &__amount {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 6px;
    background: #ffffff;
    padding: 24px 12px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    color: #1e2128;
    letter-spacing: -0.02em;
    white-space: nowrap;
  }

  &__amount-symbol,
  &__amount-value,
  &__amount-currency {
    flex-shrink: 0;
  }

  // ── Center (dark section) ────────────────────────────────
  &__center {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    background: #2a2637;
    min-height: 208px;
    overflow: hidden;
  }

  &__form-title {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 28px 12px 0;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #fafaff;
    text-align: center;
    letter-spacing: -0.01em;
  }

  &__input-row {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    padding: 20px 12px 0;
  }

  &__input-wrapper {
    position: relative;
    width: 100%;
  }

  &__input {
    width: 100%;
    padding: 16px 12px;
    background: #ffffff;
    border: 2px solid rgba(0, 0, 0, 0.28);
    border-radius: 0;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    color: transparent;
    caret-color: #1e2128;
    letter-spacing: -0.01em;
    text-align: center;
    outline: none;
    appearance: none;
    box-sizing: border-box;
    transition: border-color 0.15s ease;
    position: relative;
    z-index: 1;

    &:focus {
      border-color: rgba(0, 0, 0, 0.5);
    }

    &--error {
      border-color: #f83833 !important;
    }

    &:disabled {
      background: #deddde;
      cursor: not-allowed;
    }
  }

  &__input-mask {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 16px 12px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: -0.01em;
    pointer-events: none;
    box-sizing: border-box;
    z-index: 1;
  }

  &__input-mask-typed {
    color: #1e2128;
  }

  &__input-mask-ghost {
    color: #959395;
  }

  &__input-mask-placeholder {
    color: #959395;
  }

  &__hint {
    padding: 16px 12px 28px;
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    color: #fafaff;
    text-align: center;
    letter-spacing: -0.01em;
    white-space: pre-line;

    &--error {
      color: #f83833;
    }
  }

  &__expired-text {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: 1;
    padding: 28px 12px;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 16px;
    line-height: 20px;
    color: #fafaff;
    text-align: center;
    letter-spacing: -0.01em;
  }

  // ── Action buttons ───────────────────────────────────────
  &__actions {
    display: flex;
    align-items: stretch;
    background: #cdcdcf;
  }

  &__actions-divider {
    width: 1px;
    background: rgba(0, 0, 0, 0.28);
    flex-shrink: 0;
  }

  &__btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 48px;
    padding: 0 12px;
    background: #deddde;
    border: none;
    border-radius: 0;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 24px;
    letter-spacing: -0.01em;
    white-space: nowrap;
    transition: background 0.15s ease;

    &:active {
      background: #d0cfcf;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.8;
    }

    &--secondary {
      color: #1e2128;
    }

    &--primary {
      color: #be0000;
    }
  }
}
</style>
